import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
    Chip,
} from "@heroui/react";
import { LuHistory } from "react-icons/lu";
import { FilterData } from "./components";
import { useDataTable, useFetchData } from "@/hooks";
import {
    ActiveFilterChips,
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    FilterButton,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    ThemedText,
    PageHeader,
} from "@/components/ui";
import { formatThousands } from "@/utils";

// Initial filter values
const initialFilters = {
    warehouse_id: "",
    search: "",
    category_id: "",
    out_of_stock: "",
};

// Skeleton columns for loading state
const skeletonColumns = [
    { name: "No.", skeletonWidth: "w-12" },
    { name: "Gudang", skeletonWidth: "w-32" },
    { name: "Produk", skeletonWidth: "w-40" },
    { name: "Stok Akhir", skeletonWidth: "w-24", align: "end" },
    { name: "Aktivitas Terakhir", skeletonWidth: "w-28" },
    { name: "Aksi", skeletonWidth: "w-16", align: "end" },
];

// Helper to get stock status
const getStockChipColor = (stock) => {
    const value = Number(stock || 0);
    if (value <= 0) return { color: "danger", label: "Habis" };
    if (value <= 10) return { color: "warning", label: "Menipis" };
    return { color: "success", label: "Tersedia" };
};

const StockData = () => {
    const table = useDataTable("stocks/data", null, {}, initialFilters);

    // Fetch filter options for label lookups
    const { data: warehouses } = useFetchData("select/warehouse");
    const { data: categories } = useFetchData("select/material-category");

    // Filter options to pass to FilterData component (handle null with || [])
    const filterOptions = {
        warehouses: warehouses || [],
        categories: categories || [],
    };

    // Filter labels with dynamic getValueLabel (reuses same data)
    const filterLabels = {
        warehouse_id: {
            label: "Gudang",
            getValueLabel: (val) => {
                const item = warehouses.find(
                    (w) => String(w.value) === String(val),
                );
                return item?.title || val;
            },
        },
        search: { label: "Pencarian" },
        category_id: {
            label: "Kategori",
            getValueLabel: (val) => {
                const item = categories.find(
                    (c) => String(c.value) === String(val),
                );
                return item?.title || val;
            },
        },
        out_of_stock: {
            label: "Stok Habis",
            getValueLabel: () => "Ya",
        },
    };

    // Handle remove single filter
    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data Stok"
                    message={table.error?.message}
                    onRetry={table.refetch}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Stok Gudang"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Stok" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />
                </div>
            </div>

            {/* Filter Panel */}
            <FilterData
                table={table}
                initialFilters={initialFilters}
                filterOptions={filterOptions}
            />

            {/* Active Filter Chips */}
            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            {/* Data Table */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                {/* Legend */}
                <div className="flex items-center gap-3 px-4 py-2">
                    <ThemedText size="xs" variant="secondary">
                        Keterangan:
                    </ThemedText>
                    <div className="flex items-center gap-2">
                        <Chip size="sm" color="success" variant="flat">
                            Tersedia
                        </Chip>
                        <Chip size="sm" color="warning" variant="flat">
                            Menipis
                        </Chip>
                        <Chip size="sm" color="danger" variant="flat">
                            Habis
                        </Chip>
                    </div>
                </div>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Data Kosong"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada stok yang cocok dengan filter."
                                    : "Belum ada data stok."
                            }
                        />
                    ) : (
                        <Table aria-label="Tabel Stok Gudang" shadow="none">
                            <TableHeader>
                                <TableColumn key="no" width={50}>
                                    No.
                                </TableColumn>
                                <TableColumn key="warehouse">
                                    Gudang
                                </TableColumn>
                                <TableColumn key="material">Produk</TableColumn>
                                <TableColumn key="final_stock" align="end">
                                    Stok Akhir
                                </TableColumn>
                                <TableColumn key="last_activity">
                                    Aktivitas Terakhir
                                </TableColumn>
                                <TableColumn align="end" key="actions">
                                    Aksi
                                </TableColumn>
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow
                                        key={`${item.warehouse_id}-${item.material_id}`}
                                    >
                                        {/* No. */}
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                variant="secondary"
                                            >
                                                {table.items.indexOf(item) +
                                                    1 +
                                                    (table.pagination.page -
                                                        1) *
                                                        table.pagination
                                                            .perPage}
                                                .
                                            </ThemedText>
                                        </TableCell>
                                        {/* Warehouse */}
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {item.warehouse?.name || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        {/* Material */}
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <ThemedText size="sm">
                                                    {item.material?.name || "-"}
                                                </ThemedText>
                                                {item.material?.category && (
                                                    <ThemedText
                                                        size="xs"
                                                        variant="secondary"
                                                    >
                                                        {
                                                            item.material
                                                                .category.name
                                                        }
                                                    </ThemedText>
                                                )}
                                            </div>
                                        </TableCell>
                                        {/* Final Stock - Highlighted */}
                                        <TableCell>
                                            <Chip
                                                color={
                                                    getStockChipColor(
                                                        item.final_stock,
                                                    ).color
                                                }
                                                size="sm"
                                                variant="flat"
                                            >
                                                {formatThousands(
                                                    item.final_stock,
                                                )}{" "}
                                                {item.unit?.symbol ||
                                                    item.unit?.name ||
                                                    ""}
                                            </Chip>
                                        </TableCell>
                                        {/* Last Activity */}
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {item.last_activity_date || "-"}
                                            </ThemedText>
                                            {item.transaction_count && (
                                                <ThemedText
                                                    size="xs"
                                                    variant="secondary"
                                                >
                                                    {item.transaction_count}{" "}
                                                    transaksi
                                                </ThemedText>
                                            )}
                                        </TableCell>
                                        {/* Actions */}
                                        <TableCell>
                                            <div className="relative flex items-center justify-end gap-2">
                                                <Tooltip content="Lihat Riwayat">
                                                    <Button
                                                        as={Link}
                                                        to={`/stocks/history/warehouse/${item.warehouse_id}/material/${item.material_id}/unit/${item.material_unit_id}`}
                                                        isIconOnly
                                                        size="sm"
                                                        variant="light"
                                                    >
                                                        <LuHistory
                                                            size={14}
                                                            className="text-default-500"
                                                        />
                                                    </Button>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>
        </div>
    );
};

export default StockData;
